/* eslint-disable max-lines */

import {autorun, makeAutoObservable, observable, reaction, toJS} from 'mobx'
import {observer} from 'mobx-react-lite'
import React, {CSSProperties, ReactElement, ReactNode} from 'react'
import {Lang} from 'yougile-lang'
import {logger, Util, UUID} from 'yougile-util'
import {DataObjectId, isDataObjectId} from 'yougile-model'
import {apiBlockVisibilityStore} from '@api/components/api-block-visibility'
// API Components
import {ApiBoardSettingsCtl} from '@api/components/api-board-settings'
import {ApiDialogCtl} from '@api/components/api-dialog'
import {ApiPopupCtl} from '@api/components/api-popup'
import {ApiTaskInfoCtl} from '@api/components/api-task-info'
import {UiSlotName} from '@api/components/ui-slot'
// API Model Elements
import {IconWrapper} from '@api/model/ui-elements/icon'
import {NumberInputWrapper} from '@api/model/ui-elements/number-input'
import {ReactRenderContainer} from '@api/model/ui-elements/react'
// API Shared Components
import {SharedAttacments} from '@api/shared/components/attachments'
import {SharedBox} from '@api/shared/components/box'
import {SharedButton} from '@api/shared/components/button'
import SharedChain from '@api/shared/components/chain'
import {
  SharedDropdown,
  useDropdownRelayout
} from '@api/shared/components/dropdown'
import {SharedDropdownContent} from '@api/shared/components/dropdown-content'
import {SharedField} from '@api/shared/components/field'
import {SharedFont} from '@api/shared/components/font'
import {SharedGrid} from '@api/shared/components/grid/grid'
import {SharedHeader} from '@api/shared/components/header'
import {SharedHoverPanel} from '@api/shared/components/hover-panel'
import {SharedIcon} from '@api/shared/components/icon'
import {SharedInput} from '@api/shared/components/input'
import {SharedLoader} from '@api/shared/components/loader'
import {SharedMenu} from '@api/shared/components/menu'
import {
  SharedMultiMenu,
  SharedMultiMenuItem
} from '@api/shared/components/multi-menu'
import {SharedPortal} from '@api/shared/components/portal'
import {SharedRadioButton} from '@api/shared/components/radiobutton'
import {SharedBaseSelect} from '@api/shared/components/select'
import {SharedSticker, StickerColors} from '@api/shared/components/sticker'
import {SharedSwitcher} from '@api/shared/components/switcher'
import {SharedTable} from '@api/shared/components/table'
import {SharedUsersPicker} from '@api/shared/components/users-picker/users-picker'
import {SharedWithScrollbar} from '@api/shared/components/with-scrollbar'
// API Shared Constants & Context
import {sharedClassNames} from '@api/shared/constants/classnames'
import {cssVariable, sharedColors} from '@api/shared/constants/colors'
import {useContextDropdown} from '@api/shared/context/context-dropdown'
import {useContextTask} from '@api/shared/context/context-task'
import {useUiLocation} from '@api/shared/context/ui-location'
import {sharedShowConfirm} from '@api/shared/tools/show-confirm'
import {StateStore} from '@api/tools/state-store'
// Common Components
import {Backend} from '@common/backend'
import {FileChooser} from '@common/file-chooser'
import {FilePreview} from '@common/file-preview'
// Components
import {BoardTabBg} from '@components/board/board-tab-bg'
import {Avatar} from '@components/common/base/avatar'
import {IconButton, LinkButton} from '@components/common/base/buttons'
import {TriCheckboxNew} from '@components/common/base/checkbox-new'
import {CheckboxSize} from '@components/common/base/checkbox-new/types'
import {allColors} from '@components/common/base/design-system'
import {Dialog, DialogProps} from '@components/common/base/dialog/dialog'
import {IconSvg} from '@components/common/base/icon-svg'
import {showDeleteConfirm} from '@components/common/base/notifications/show-confirm'
import {Popup} from '@components/common/base/popup'
import {
  openPopup,
  OpenPopupReturnType,
  PropsGetter
} from '@components/common/base/popup/open-popup'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {SelectWithDropdownProps} from '@components/common/base/select-with-dropdown/select-with-dropdown'
import {TextareaLite} from '@components/common/base/textarea/lite'
import {
  Color,
  Paddings,
  Size,
  TextAreaApi
} from '@components/common/base/textarea/types'
import {
  DatePicker,
  DateRangePicker
} from '@components/common/date-picker/date-picker'
import {IconType, TablerIcons} from '@components/common/icon-tabler/icon-tabler'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {FileUploadProgress} from '@components/file-upload-progress'
import {MessageBox} from '@components/message-box'
import {DestinationRet} from '@components/movement-templates/destination/types'
import {
  SelectedColumns,
  SelectedColumnsProps
} from '@components/movement-templates/selected-columns'
// Containers & Extensions
import {cn} from '@containers/html-utils'
import {UserStickerState} from '@extensions/sticker/views-web/user/user-sticker-state'
import {
  getExtensionPublicUrl,
  getRightUrl
} from '@extensions/web-platform/get-right-url'
import {
  UserActivityMonitor,
  UserActivityTracker
} from '@extensions/web-platform/user-activity-tracker'
// Local imports
import {ContextObject} from '../context-object'
import {Item} from './item'
import {UIElement} from './ui-element'
import {ButtonEl} from './ui-elements/button'
import {ButtonNewWrapper} from './ui-elements/button-new'
import {CheckboxWrapper} from './ui-elements/checkbox'
import {ColumnSelectWrapper} from './ui-elements/column-select'
import {DateInputProps, DateInputWrapper} from './ui-elements/date-input'
import {DestinationWrapper} from './ui-elements/destination'
import {ImageEl} from './ui-elements/image'
import {InputEl} from './ui-elements/input'
import {InputNewWrapper} from './ui-elements/input-new'
import {LabelEl} from './ui-elements/label'
import {LinkEl} from './ui-elements/link'
import {LinkButtonWrapper} from './ui-elements/link-button'
import {PanelEl} from './ui-elements/panel'
import {RawHtmlEl} from './ui-elements/raw-html'
import {SafeHtmlEl} from './ui-elements/safe-html'
import {SelectWrapper} from './ui-elements/select'
import {TextEl} from './ui-elements/text'
import {TextareaWrapper} from './ui-elements/textarea'
import {User} from './ui-elements/user'
import {UserSelect, UserSelectOptions} from './ui-elements/user-select'
import {Disposer, UIRegistry} from './ui-registry'
import {UserObj} from './user-obj'

export type UIRenderer = (id?: string) => UIElement | undefined

export const apiUiCategories = [
  'chat-nav',
  'chat-messages-bottom',
  'task-header-menu',
  'task-header-bottom',
  'column-header',
  'column-header-after-title',
  'column-header-after-menu',
  'task-chat-list-header'
] as const

export type ApiUiCategoryName = (typeof apiUiCategories)[number]

// ApiUiCategoryName:DataObjectId
export type ApiUiCategory = ApiUiCategoryName | string

const isApiUiCategory = (category: string): category is ApiUiCategory => {
  const categoryByDelimiter = category.split(':')

  if (categoryByDelimiter.length === 1) {
    return apiUiCategories.includes(category as ApiUiCategoryName)
  }

  if (categoryByDelimiter.length > 2) {
    return false
  }

  const [categoryType, id] = category.split(':') as [
    ApiUiCategoryName,
    DataObjectId
  ]

  return apiUiCategories.includes(categoryType) && isDataObjectId(id)
}

export const getExtensionSettingsKey = (extId: string): DataObjectId =>
  ('extensionSettings::' + extId) as DataObjectId

interface UIComponents {
  chat: UIElement
  stickerPanel: UIElement
  extensionSettings: UIElement
}

/**
 * @description
 * Allows to customize components of user interface.
 * Also allows to create new user interface elements and to add new
 * interfaces inside existing ones.
 * @descriptionRu
 * Позволяет кастомизировать компоненты пользовательского интерфейса.
 * Также позволяет создавать новые элементы интерфейса и добавлять новые
 * элементы интерфейса внутрь имеющихся.
 */
export class UI extends ContextObject {
  /**
   * @description
   * Version of the UI module. The extensions can use this property to check if required features are available.
   * Every time the UI module API is updated, the version is incremented. It guarantees that extensions will work correctly with the new features.
   * @descriptionRu
   * Версия модуля UI. Расширения могут использовать это свойство для проверки наличия необходимых функций.
   * Каждый раз, когда обновляется API модуля UI, версия увеличивается. Это гарантирует, что расширения будут работать корректно с новыми функциями.
   */
  override version = '1.0.0'
  /**
   * @description
   * React component for rendering a attachments (files) in the form
   * @descriptionRu
   * React-компонент для отрисовки вложений (файлов) в форме
   */
  Attachments = SharedAttacments
  /**
   * @description
   * React component for rendering a button
   * @descriptionRu
   * React-компонент для отрисовки кнопки
   */
  Button = SharedButton
  /**
   * @description
   * React component for rendering a portal
   * @descriptionRu
   * React-компонент для отрисовки портала
   */
  Portal = SharedPortal
  /**
   * @description
   * React component for rendering an input
   * @descriptionRu
   * React-компонент для отрисовки инпута
   */
  Input = SharedInput
  /**
   /**
   * TODO: Move TextareaWrapper to app/api/model/shared/components
   * @description
   * React component for rendering an textarea
   * @descriptionRu
   * React-компонент для отрисовки поля для текста
   */
  Textarea = TextareaWrapper
  /**
   * @description
   * React component for rendering a checkbox
   * @descriptionRu
   * React-компонент для отрисовки чекбокса
   */
  Checkbox = TriCheckboxNew
  /**
   * @description
   * React component for rendering a column selector
   * @descriptionRu
   * React-компонент для отрисовки селектора колонок
   */
  SelectColumns = SelectedColumns
  /**
   * @description
   * React component for rendering a tooltip
   * @descriptionRu
   * React-компонент для отрисовки тултипа
   */
  Tooltip = Tooltip
  /**
   * @description
   * React component for rendering a button which looks like a link
   * @descriptionRu
   * React-компонент для отрисовки кнопки, которая выглядит как ссылка
   */
  LinkButton = LinkButton
  /**
   * @description
   * React component for rendering a icon button
   * @descriptionRu
   * React-компонент для отрисовки кнопки-иконки
   */
  IconButton = IconButton
  /**
   * @description
   * React component for rendering a icon
   * @descriptionRu
   * React-компонент для отрисовки иконки
   */
  Icon = SharedIcon
  /**
   * @description
   * React component for rendering a users selector.
   * @descriptionRu
   * React-компонент для отрисовки поля выбора пользователей.
   */
  SelectUsers = UserStickerState
  /**
   * @description
   * React component for rendering a select field with dropdown
   * @descriptionRu
   * React-компонент для отрисовки поля выбора с выпадающим списком
   */
  SelectWithDropdown = SelectWithDropdown
  /**
   * @exprimental
   * @description
   * React component for rendering a customizable popup
   * @descriptionRu
   * React-компонент для отрисовки настраиваемого всплывающего окна
   */
  Popup = Popup
  /**
   * @exprimental
   * @description
   * React component for rendering a customizable dialog
   * @descriptionRu
   * React-компонент для отрисовки настраиваемого всплывающего окна
   */
  Dialog = Dialog
  /**
   * @description
   * React component for rendering a text area
   * @descriptionRu
   * React-компонент для отрисовки текстовой области
   */
  TextArea = TextareaLite

  /**
   * @description
   * React component for rendering a sticker
   * @descriptionRu
   * React-компонент для отрисовки стикера
   */
  Sticker = SharedSticker

  /**
   * @description
   * Sticker colors map. Allows to pick a color for a sticker.
   * @descriptionRu
   * Карта цветов стикеров. Позволяет выбрать цвет для стикера.
   */
  StickerColors = StickerColors

  /**
   * @description
   * React component for rendering an downdown popup with content for any element
   * @descriptionRu
   * React-компонент для отрисовки выпадающего попапа с содержимым для любого элемента
   */
  Dropdown = SharedDropdown

  /**
   * @description
   * Hook for manually recalculating dropdown position
   * @descriptionRu
   * Хук для ручного пересчета позиции дропдауна
   */
  useDropdownRelayout = useDropdownRelayout

  /**
   * @description
   * Panel component suitable for displaying as a sticker menu content, or any panel inner markup with a title and content
   * @descriptionRu
   * Компонент панели, подходящий для отображения в качестве меню наклеек или любой панели с заголовком и содержимым
   */
  DropdownContent = SharedDropdownContent
  /**
   * @description
   * React component for rendering an user avatar
   * @descriptionRu
   * React-компонент для отрисовки автара пользователя
   */
  Avatar = Avatar
  /**
   * @description
   * React component for rendering a date picker
   * @descriptionRu
   * React-компонент для отрисовки выбора даты
   */
  DatePicker = DatePicker
  /**
   * @description
   * React component for rendering a date range picker
   * @descriptionRu
   * React-компонент для отрисовки выбора диапазона дат
   */
  DateRangePicker = DateRangePicker
  /**
   * @description
   * React component for rendering a table
   * @descriptionRu
   * React-компонент для отрисовки таблицы
   */
  Table = SharedTable
  /**
   * @deprecated Use UI.BaseSelect instead
   * @description
   * React component for rendering an select view with fully customizable dropdown content
   * @descriptionRu
   * React-компонент для отрисовки представления селекта с полностью настраиваемым содержимым выпадающего списка
   */
  RawSelect = SharedBaseSelect
  /**
   * @description
   * React component for rendering an select view with fully customizable dropdown content
   * @descriptionRu
   * React-компонент для отрисовки представления селекта с полностью настраиваемым содержимым выпадающего списка
   */
  BaseSelect = SharedBaseSelect
  /**
   * @description
   * React component for rendering the users select view
   * @descriptionRu
   * React-компонент для отрисовки представления выбора пользователей
   */
  UsersPicker = SharedUsersPicker
  /**
   * @descriptionRu
   * React-компонент для отрисовки иконки используя svg
   * Пример использования:
   * <UI.IconSvg src="/extensions/integration-with-telegram/icon.svg" size={24} />
   */
  IconSvg = IconSvg
  /**
   * @description
   * React component for rendering a header
   * @descriptionRu
   * React-компонент для отрисовки заголовка
   */
  Header = SharedHeader
  Field = SharedField
  /**
   * @description
   * React component for rendering a menu (useful for creating dropdown content)
   * @descriptionRu
   * React-компонент для отрисовки меню (полезно для создания содержимого выпадающего списка)
   */
  Menu = SharedMenu
  /**
   * @description
   * Menu with nested menus
   * @descriptionRu
   * Меню с вложенными меню
   */
  MultiMenu = SharedMultiMenu
  /**
   * @description
   * React component for rendering a menu item (used in MultiMenu) separately
   * @descriptionRu
   * React-компонент для отрисовки элемента меню (используется в MultiMenu) отдельно
   */
  MultiMenuItem = SharedMultiMenuItem
  /**
   * @description
   * React component for rendering a chain of elements
   * @descriptionRu
   * React-компонент для отрисовки цепочки элементов
   */
  Chain = SharedChain
  /**
   * @description
   * Helper component for making padding, margins and flexbox behaviour
   * @descriptionRu
   * Вспомогательный компонент для создания отступов, полей и поведения flexbox
   */
  Box = SharedBox
  /**
   * @description
   * Allows to set a font style for the text
   */
  Font = SharedFont
  /**
   * @description
   * Wrap with scrollbar component
   * @descriptionRu
   * Обернуть компонент в скроллбар
   */
  WithScroll = SharedWithScrollbar
  /**
   * @description
   * React component for rendering an hover panel
   * @descriptionRu
   * React-компонент для отрисовки панели при наведении
   */
  HoverPanel = SharedHoverPanel
  /**
   * @description
   * Radio button
   * @descriptionRu
   * Радио-кнопка
   */
  RadioButton = SharedRadioButton
  /**
   * @description
   * Loader component
   * @descriptionRu
   * Компонент загрузки
   */
  Loader = SharedLoader
  /**
   * @description
   * CSS Grid component for creating grid layouts
   * @descriptionRu
   * Компонент CSS Grid для создания сеточных макетов
   */
  CssGrid = SharedGrid
  /**
   * @description
   * Switcher component (like checkbox but with different style)
   * @descriptionRu
   * Компонент переключателя (как чекбокс, но с другим стилем)
   */
  Switcher = SharedSwitcher
  /**
   * @description
   * Hook for getting the current context task data (if available)
   * @descriptionRu
   * Хук для получения данных текущей задачи из контекста (если доступно)
   */
  useContextTask = useContextTask
  /**
   * @description
   * Hook for getting the current context dropdown data (if available)
   * @descriptionRu
   * Хук для получения данных текущего выпадающего списка из контекста (если доступно)
   */
  useContextDropdown = useContextDropdown
  /**
   * @description
   * Hook for getting current location in the UI structure (if specified)
   * @descriptionRu
   * Хук для получения текущего местоположения в структуре пользовательского интерфейса (если указано)
   */
  useLocation = useUiLocation
  /**
   * @deprecated Use mobx instaad
   * @description
   * Mobx observer decorator (compatible with store created with createStore)
   * @descriptionRu
   * Декоратор Mobx observer (совместим с хранилищем, созданным с помощью createStore)
   */
  observer = observer
  /**
   * @description
   * Mobx tools
   * @descriptionRu
   * Инструменты Mobx
   */
  mobx = {
    observer,
    makeAutoObservable,
    observable,
    autorun,
    reaction,
    toJS
  }

  /**
   * @description
   * Allows to show a simple confirmation dialog
   */
  showConfirm = sharedShowConfirm

  /**
   * @description
   * Shows modal dialog to confirm delete operation.
   * @descriptionRu
   * Показывает модальное окно для подтверждения операции удаления.
   */

  /**
   * @todo Replace with configurable showConfirm
   */
  showDeleteConfirm = showDeleteConfirm

  /**
   * @description
   * Collection of shared class names for UI elements
   * @descriptionRu
   * Коллекция общих классов для элементов интерфейса
   */
  classNames = sharedClassNames
  /**
   * @description
   * Collection of keys of shared colors for UI elements
   * @descriptionRu
   * Коллекция ключей цветов для элементов интерфейса
   */
  colors = sharedColors

  /**
   * @description
   * Returns a color css variable by its key
   */
  cssVariable = cssVariable

  /**
   * @description
   * Collection of utils for working with UI
   * @descriptionRu
   * Коллекция утилит для работы с интерфейсом
   */
  utils = {
    /**
     * @description
     * Combines class names into a single string
     * @descriptionRu
     * Объединяет имена классов в одну строку
     */
    cn: cn
  }

  public static override registered: Map<DataObjectId, Map<string, UI>> =
    new Map()

  private created: UIElement[] = []

  /**
   * @todo implement
   */
  public onRender: (object: Item) => UIElement

  /**
   * @todo implement
   */
  public onDestroy: (object: Item) => UIElement

  /**
   * @code
   * UI.components.chat.style = {
   *
   * };
   * @description
   * Collection of user interface components of the system
   * (see usage example below).
   * @descriptionRu
   * Набор компонентов интерфейса системы (см. примеры использования ниже).
   * @example
   * // chat window contents
   * UI.components.chat;
   *
   * // panel of stickers in board
   * UI.components.stickerPanel;
   */
  public get components(): UIComponents {
    const uiReg = UIRegistry.get(this.context.box.companyId)

    const comp = (name: string): UIElement => {
      const id = name as DataObjectId
      let res: UIElement | undefined
      if (uiReg.uiRegistry.has(id)) {
        res = uiReg.uiRegistry.get(id)
      } else {
        const el = new UIElement(this.context.box.companyId, id, undefined)
        uiReg.uiRegistry.set(id, el)
        res = el
      }
      if (!res) {
        throw new Error(`UI element not found ${id}`)
      }
      return res
    }

    const extensionSettingsKey = getExtensionSettingsKey(
      this.context.extensionId
    )

    return {
      chat: comp('chat'),
      stickerPanel: comp('sticker-panel'),
      extensionSettings: comp(extensionSettingsKey)
    }
  }

  /**
   * @code
   * UI.background = '... url';
   * @description
   * Allows to set a custom background. Background should be
   * loaded to YouGile.
   * To set a background you should specify relative url.
   * If background is set to empty string then it returns to its default.
   * @descriptionRu
   * Позволяет устанавливать свой фон. Фон должен быть загружен в YouGile.
   * Чтобы установить фон, вы должны указать соответствующий url.
   * Если указывается пустая строка, то фон возвращается к установленному
   * по умолчанию.
   * @example
   * // custom background for a particular board example
   *
   * // getting the board
   * var board = Items.get('... board id');
   * // custom image
   * var img =
   *   '/user-data/10c3ddff-ce6a-43a0-8fbb-3e1d6ba3a245/my-image.jpg';
   * // to get its url you can upload it to YouGile
   * // set it as a background and call console.log(UI.background);
   *
   * // we need to set the background at the start of the script…
   * if (Current.board.id === board.id) {
   *   UI.background = img;
   * } else {
   *   UI.background = ''; // set default for other boards
   * }
   *
   * // …and also change the background if user navigates to
   * // another board
   * Current.onBoardChange = function (oldBoard, newBoard) {
   *   if (newBoard && newBoard.id === board.id) {
   *     UI.background = img;
   *   } else {
   *     UI.background = ''; // set default for other boards
   *   }
   * };
   */
  public get background(): string {
    return BoardTabBg.getSkin()
  }

  public set background(val: string) {
    void BoardTabBg.setCustom(val)
  }

  /**
   * @code
   * UI.render('', id => {
   *   return UI.text('');
   * });
   * @description
   * Allows to add custom UI elements to the specified location in
   * the user interface, these locations are marked with data attributes,
   * for example: `data-ui="task-header-menu:d18d7ae5-3390-40ae-b7f7-5300ca187010"`
   * – you can see data attributes like this in dom elements.
   * @descriptionRu
   * Позволяет добавлять свои UI элементы в указанную локацию в
   * пользовательском интерфейсе, эти локации отмечены data-атрибутами,
   * например: `data-ui="task-header-menu:d18d7ae5-3390-40ae-b7f7-5300ca187010"`
   * Можно открыть DevTools и в DOM увидеть элементы с такими атрибутами, по всему
   * интерфейсу есть много таких элементов, это позволяет добавлять свой UI в разные части системы.
   * Для оптимизации производительности можно конкретизировать категорию с помощью DataObjectId,
   * в таком случае category может быть вида `category:id`
   * @example
   * // example of custom ui element
   * UI.render('task-header-menu', id => {
   *   return UI.text('Example ' + id);
   * });
   */
  render(category: ApiUiCategory, render: UIRenderer): () => void {
    if (!isApiUiCategory(category)) {
      throw new Error(
        `Invalid ui category ${category}, available categories: ${apiUiCategories.join(
          ', '
        )}`
      )
    }
    const uiReg = UIRegistry.get(this.context.box.companyId)
    const renderers = uiReg.uiRenderers
    renderers.set(category, render)
    const {extensionId, scriptName} = this.context
    if (extensionId || scriptName) {
      uiReg.addExtensionCategory(extensionId || scriptName, category)
    }
    uiReg.refresh(category)

    return () => {
      if (extensionId || scriptName) {
        uiReg.removeExtensionCategory(extensionId || scriptName, category)
      }
      renderers.delete(category)
      uiReg.refresh(category)
    }
  }

  /**
   * @code
   * UI.text('...');
   * @example
   * var t = Items.get('... task id');
   * var txt = UI.text('Some text…');
   * txt.style.color = 'red';
   * t.ui.add(txt);
   *
   * // we can also change the text of this label
   * t.ui.onClick = function () {
   *   txt.text = 'Clicked!';
   *   txt.style.color = '#0c0';
   * }
   * @description
   * Creates a text label element.
   * @descriptionRu
   * Создает элемент "текст".
   */
  public text(value: string): UIElement {
    const res = new TextEl(this.context.box.companyId, value)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.button({ title: '...', size: 'sm', color: 'primary' });
   * @example
   * var t = Items.get('... task id');
   * var btn = UI.button({
   *   title: 'Submit',
   *   size: 'lg',
   *   color: 'secondary',
   *   icon: 'IconCustomTimer',
   *   hint: 'Click to submit'
   * });
   * btn.style.backgroundColor = 'green';
   * t.ui.add(btn);
   *
   * // Add event handling for the button
   * btn.onClick = function (e) {
   *   console.log('Button clicked', e);
   * }
   *
   * // Change button state on mouse down
   * btn.onMouseDown = function (e) {
   *   btn.title = 'Pressed';
   * }
   * @description
   * Creates a customizable button element with various properties like icon, title, size, color, and event handlers.
   * @descriptionRu
   * Создает кнопку с различными свойствами, такими как иконка, заголовок, размер, цвет.
   */
  public button(props: {
    icon?: TablerIcons
    iconAfter?: TablerIcons
    title?: React.ReactNode
    hint?: React.ReactNode
    size?: 'sm' | 'md' | 'lg'
    color?: 'primary' | 'secondary' | 'notification-primary'
    disabled?: boolean
    onClick?: (e: React.MouseEvent) => void
    onMouseDown?: (e: React.MouseEvent) => void
  }): UIElement {
    if (typeof props === 'string') {
      const res = new ButtonEl(this.context.box.companyId, props)
      this.created.push(res)
      return res
    }

    const res = new ButtonNewWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  public buttonNew(props: {
    icon?: TablerIcons
    iconAfter?: TablerIcons
    title?: React.ReactNode
    hint?: React.ReactNode
    size?: 'sm' | 'md' | 'lg'
    color?: 'primary' | 'secondary' | 'notification-primary'
    disabled?: boolean
    onClick?: (e: React.MouseEvent) => void
    onMouseDown?: (e: React.MouseEvent) => void
  }): UIElement {
    const res = new ButtonNewWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  public destination(props: {
    onChange: (props: DestinationRet) => void
    initData?: DestinationRet
  }): UIElement {
    const res = new DestinationWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * const checkbox = UI.checkbox({ value: true });
   * @example
   * // Basic checkbox
   * const checkbox = UI.checkbox({ value: true });
   *
   * // Checkbox with label, footnote, and color options
   * const checkbox = UI.checkbox({
   *   value: true,
   *   onChange: (newValue) => console.log('Checkbox state changed:', newValue),
   *   label: 'Accept Terms',
   *   footnote: 'You must accept terms to continue',
   *   color: 'blue',
   *   rounded: true
   * });
   * @description
   * Creates a checkbox with customizable options for appearance and behavior.
   * Optional properties can be provided in the props parameter:
   * - value (boolean): Initial checked state of the checkbox.
   * - onChange (function): Callback function triggered on state change. Receives the new value
   *   and an optional event (MouseEvent or KeyboardEvent).
   * - disabled (boolean): If true, the checkbox is disabled and cannot be interacted with.
   * - error (boolean): If true, displays the checkbox in an error state.
   * - label (React.ReactNode): Text or element label displayed beside the checkbox.
   * - footnote (React.ReactNode): Footnote displayed below the checkbox for additional context.
   * - renderFullFootnote (boolean): If true, renders the footnote in full width below the checkbox.
   * - className (string): Additional CSS class for styling the checkbox wrapper.
   * - labelClassName (string): Additional CSS class for styling the label.
   * - dataTestId (string): ID for testing purposes.
   * - color ('green' | 'blue' | 'gray'): Color theme of the checkbox.
   * - rounded (boolean): If true, renders the checkbox with rounded edges.
   * - size (CheckboxSize): Size of the checkbox; available options are defined in CheckboxSize.
   * @descriptionRu
   * Создает флажок с возможностью настройки внешнего вида и поведения.
   * Дополнительные параметры можно передать в объекте props:
   * - value (boolean): Начальное состояние флажка (отмечен или нет).
   * - onChange (function): Обработчик события изменения состояния, получает новое значение value
   *   и необязательный объект события (MouseEvent или KeyboardEvent).
   * - disabled (boolean): Если true, флажок отключен и не доступен для взаимодействия.
   * - error (boolean): Если true, флажок отображается в состоянии ошибки.
   * - label (React.ReactNode): Текст или элемент, отображаемый рядом с флажком.
   * - footnote (React.ReactNode): Пояснение под флажком.
   * - renderFullFootnote (boolean): Если true, пояснение отображается на всю ширину под флажком.
   * - className (string): Дополнительный CSS-класс для обертки флажка.
   * - labelClassName (string): Дополнительный CSS-класс для метки.
   * - dataTestId (string): ID для целей тестирования.
   * - color ('green' | 'blue' | 'gray'): Цветовая тема флажка.
   * - rounded (boolean): Если true, флажок отображается с закругленными краями.
   * - size (CheckboxSize): Размер флажка, возможные значения определены в CheckboxSize.
   */
  public checkbox(props: {
    value: boolean
    onChange?: (
      value: boolean,
      e?: React.MouseEvent | React.KeyboardEvent
    ) => void
    disabled?: boolean
    error?: boolean
    label?: React.ReactNode
    footnote?: React.ReactNode
    renderFullFootnote?: boolean
    className?: string
    labelClassName?: string
    dataTestId?: string
    color?: 'green' | 'blue' | 'gray'
    rounded?: boolean
    size?: CheckboxSize
  }): UIElement {
    const res = new CheckboxWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * const myIcon = UI.icon({ icon: 'star' });
   * @example
   * // Basic icon
   * const myIcon = UI.icon({ icon: 'star' });
   *
   * // Icon with custom color, size, and tooltip
   * const myIcon = UI.icon({
   *   icon: 'star',
   *   color: 'blue',
   *   size: 24,
   *   title: 'Favorite',
   *   hint: 'Click to add to favorites'
   * });
   *
   * @description
   * Creates an icon with customizable appearance and behavior. Optional properties
   * can be provided in the icon parameter:
   * - color (keyof typeof allColors): Sets the color of the icon from the available color palette.
   * - size (12 | 16 | 20 | 24 | 32): Specifies the icon size in pixels.
   * - type (IconType): Specifies the type of the icon, allowing for different styles or designs.
   * - icon (TablerIcons): Required. Specifies the icon from the available TablerIcons set.
   * - className (string): Adds a custom CSS class for styling the icon element.
   * - title (string): Provides a title for accessibility, displayed as a tooltip on hover.
   * - hint (ReactNode): Additional hint or tooltip displayed on hover for more context.
   * - style (CSSProperties): Applies inline styles directly to the icon.
   * - onClick ((e: React.MouseEvent) => void): Callback function triggered when the icon is clicked, receiving the event as an argument.
   *
   * @descriptionRu
   * Создает иконку с возможностью настройки внешнего вида и поведения.
   * Поддерживает иконки из https://tabler.io/icons (более 5000 иконок)
   * Дополнительные параметры можно передать в объекте icon:
   * - color (ключ из allColors): Устанавливает цвет иконки из доступной цветовой палитры.
   * - size (12 | 16 | 20 | 24 | 32): Размер иконки в пикселях.
   * - type (IconType): Устанавливает тип иконки, позволяя выбирать различные стили.
   * - icon (TablerIcons): Обязательный. Указывает иконку из доступного набора TablerIcons.
   * - className (строка): Добавляет пользовательский CSS-класс для стилизации элемента иконки.
   * - title (строка): Заголовок для доступности, отображается как подсказка при наведении.
   * - hint (ReactNode): Дополнительная подсказка или всплывающая подсказка для контекста.
   * - style (CSSProperties): Применяет встроенные стили непосредственно к иконке.
   * - onClick (функция): Обработчик события нажатия на иконку, получает объект события.
   */
  public icon(icon: {
    color?: keyof typeof allColors
    size?: 12 | 16 | 20 | 24 | 32
    type?: IconType
    icon: TablerIcons
    className?: string
    title?: string
    hint?: ReactNode
    style?: CSSProperties
    onClick?: (e: React.MouseEvent) => void
  }): UIElement {
    const res = new IconWrapper(this.context.box.companyId, icon)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.input({ value: '...', type: 'text', placeholder: 'Enter text' });
   * @example
   * const t = Items.get('... task id');
   *
   * const input = UI.input({
   *   type: 'text',
   *   placeholder: 'Enter your name',
   *   size: 'md',
   *   iconRight: 'IconCustomStarMark',
   *   label: 'Name',
   *   support: 'This is your display name',
   *   onChange: value => {
   *     console.log('Input changed:', value)
   *   },
   *   onIconRightClick: () => {
   *     console.log('Icon clicked')
   *   },
   *   iconRightError: true
   * })
   * t.ui.add(input);
   *
   * @description
   * Creates a customizable input field with various properties like value, type, placeholder, icons, labels, error state, and event handlers for focus, blur, key events, and icon actions.
   * The component can be controlled by controlled is true. A controlled component requires passing the current value in the value parameter.
   * @descriptionRu
   * Создает настраиваемое поле ввода с различными свойствами, такими как значение, тип, плейсхолдер, иконки, метки, состояние ошибки, а также обработчики событий для фокуса, выхода, клавиш и действий с иконками.
   * Компонент может быть управляемым, если передать controlled true. Управляемый компонент требует передачи актуального значения в параметре value при помощи метода rerender.
   * @param props
   */
  public input(props: {
    value: string
    type?: 'number' | 'text'
    size?: 'md' | 'lg'

    onChange?: (value: string) => void
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void

    placeholder?: string
    autoFocus?: boolean
    isAutoWidth?: boolean

    className?: string
    inputClassName?: string
    disabled?: boolean

    textInputStyle?: Partial<CSSStyleDeclaration>
    controlled?: boolean
  }): UIElement {
    if (typeof props === 'undefined') {
      const res = new InputEl(this.context.box.companyId)
      this.created.push(res)
      return res
    }

    const res = new InputNewWrapper(
      this.context.box.companyId,
      props,
      props.controlled ?? false
    )
    this.created.push(res)
    return res
  }

  /**
   * @deprecated
   */
  public inputNew(props: {
    value: string
    type?: 'number' | 'text'
    size?: 'md' | 'lg'

    onChange?: (value: string) => void
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void

    placeholder?: string
    autoFocus?: boolean
    isAutoWidth?: boolean

    className?: string
    inputClassName?: string
    disabled?: boolean

    textInputStyle?: Partial<CSSStyleDeclaration>
    controlled?: boolean
  }): UIElement {
    const res = new InputNewWrapper(
      this.context.box.companyId,
      props,
      props.controlled ?? false
    )
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.numberInput({ value: 1 });
   * @example
   * const t = Items.get('... task id');
   *
   * const input = UI.numberInput({
   *   value: 1,
   *   min: 1,
   *   max: 10,
   *   onChange: value => {
   *     console.log('Input changed:', value)
   *   }
   * })
   * t.ui.add(input);
   *
   * @description
   * Creates a custom numeric input field with different properties such as value, min, max.
   * @descriptionRu
   * Создает настраиваемое числовое поле ввода с различными свойствами, такими как значение, минимальное, максимальное значение.
   */
  public numberInput(props: {
    value: number
    step?: number
    min?: number
    max?: number
    onChange: (value: number) => void
    className?: string
    inputClassName?: string
    disabled?: boolean
    isPlusMinusVisible?: boolean
  }): UIElement {
    const res = new NumberInputWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.textarea({value: '1'})
   * @example
   * const textarea = UI.textarea({
   *   autoFocus: true,
   *   allowNewLine: true,
   *   className: 'w-full',
   *   textAreaClassName: 'py-8 min-h-32',
   *   textSize: 'medium',
   *   onChange: api => {
   *     console.log('Textarea changed', api.text)
   *   },
   *   value: 'Stored data',
   *   allowSoftNewLine: true,
   *   label: 'Enter login',
   *   placeholder: 'Login'
   * })
   * @description
   * Creates a textarea with support multiple lines.
   * @descriptionRu
   * Создает многострочный инпут.
   */
  public textarea(props: {
    textSize?: Size
    color?: Color

    autoFocus?: boolean | undefined
    placeholder?: string | undefined
    value?: string | ReadonlyArray<string> | number | undefined
    defaultValue?: string | number | ReadonlyArray<string> | undefined
    disabled?: boolean

    className?: string
    textAreaClassName?: string
    style?: React.CSSProperties

    autoComplete?: string | undefined
    allowNewLine?: boolean
    allowSoftNewLine?: boolean
    borders?: boolean
    paddings?: Paddings
    noBackground?: boolean

    onSubmit?: (api: TextAreaApi) => void
    onCancel?: (api: TextAreaApi) => void
    onFocus?: (api: TextAreaApi) => void
    onBlur?: (api: TextAreaApi) => void
    onChange?: (api: TextAreaApi) => void
    onPaste?: (api: TextAreaApi) => void
    onInit?: (element: HTMLTextAreaElement) => void

    hasError?: boolean
    isCorrect?: boolean
  }): UIElement {
    const res = new TextareaWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.label('...');
   * @example
   * var t = Items.get('... task id');
   * var checkbox = UI.checkbox('case1-id');
   * var label = UI.label('case1-id', 'checkbox-text');
   * var value = false; // counter of the button clicks
   * checkbox.onChange = function (newValue) {
   *   value = newValue
   *   t.ui.add(UI.text('Current value': newValue));
   * };
   * t.ui.add(checkbox);
   * t.ui.add(label);
   * @description
   * Creates a checkbox with label.
   * @descriptionRu
   * Создает чекбокс с надписью.
   */
  public label(id: string, text: string): UIElement {
    const res = new LabelEl(this.context.box.companyId, id, text)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.image('... image url');
   * @example
   * var t = Items.get('... task id');
   * var im = UI.image('https://goo.gl/KR53QM');
   * t.ui.add(im);
   * @description
   * Creates image element.
   * @descriptionRu
   * Создает элемент "изображение".
   */
  public image(src: string): UIElement {
    const res = new ImageEl(this.context.box.companyId, src)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.link('... text', '... url');
   * @example
   * var t = Items.get('... task id');
   * var a = UI.link('Google it!', 'https://google.com');
   * t.ui.add(a);
   * @description
   * Creates a hypertext link.
   * @descriptionRu
   * Создает ссылку.
   */
  public link(text: string, url: string): UIElement {
    const res = new LinkEl(this.context.box.companyId, text, url)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.linkButton({ text: '...', size: 'medium' });
   * @example
   * var t = Items.get('... task id');
   * var linkBtn = UI.linkButton({
   *   text: 'Go to page',
   *   size: 'big',
   *   icon: TablerIcons.ArrowRight,
   * });
   * linkBtn.style.color = 'blue';
   * t.ui.add(linkBtn);
   *
   * // Add event handling for mouse down
   * linkBtn.onMouseDown = function (e) {
   *   console.log('Link button pressed', e);
   * }
   * @description
   * Creates a customizable link button with text, size, icon, and an optional class name. You can also define behavior for mouse down events.
   * @descriptionRu
   * Создает настраиваемую ссылку-кнопку с текстом, размером, иконкой.
   */
  public linkButton(props: {
    text?: string
    size?: 'small' | 'medium' | 'big'
    icon?: TablerIcons
    className?: string
    onMouseDown?: (e: React.MouseEvent) => void
  }): UIElement {
    const res = new LinkButtonWrapper(this.context.box.companyId, props)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.user(Users.get('...'));
   * @description
   * Creates a user photo icon with the specified user.
   * @descriptionRu
   * Создаёт иконку с аватаром пользователя.
   */
  public user(user: UserObj, size: number): UIElement {
    if (!user) {
      return new TextEl(this.context.box.companyId, '')
    }
    const res = new User(this.context.box.companyId, user.id, size)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.userSelect({});
   * @description
   * Creates user select element.
   * @descriptionRu
   * Создаёт элемент выбора пользователя из списка.
   * @example
   * // all parameters are optional
   * UI.userSelect({
   *   available: ['user1 id', 'user2 id'],
   *   chosen: ['user1 id'],
   *   onChoose: function (opts) {
   *     console.log('Chosen user ids' opts);
   *   },
   *   multiChoice: true,
   *   size: 32,
   *   title: 'Users'
   * });
   */
  public userSelect(param: UserSelectOptions): UIElement {
    const res = new UserSelect(this.context.box.companyId, param)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.columnsSelect({});
   * @description
   * Creates a column selection element.
   * @descriptionRu
   * Создаёт элемент для выбора колонок.
   * @example
   * // all parameters are optional
   * UI.columnsSelect({
   *   onChange: function (props) {
   *     console.log('Selected columns', props);
   *   },
   *   columns: [
   *     {
   *       id: '000071aa-fe2d-436a-bb31-489d26f4e4b8',
   *       key: '000071aa-fe2d-436a-bb31-489d26f4e4b8',
   *       text: 'Column 1',
   *       selected: true
   *     },
   *     {
   *       id: '000071aa-fe2d-436a-bb31-489d26f4e4b1',
   *       key: '000071aa-fe2d-436a-bb31-489d26f4e4b1',
   *       text: 'Column 2',
   *       selected: false
   *     }
   *   ]
   * });
   * @param param
   */
  public columnsSelect(
    param: SelectedColumnsProps & {controlled?: boolean}
  ): UIElement {
    const res = new ColumnSelectWrapper(
      this.context.box.companyId,
      param,
      param.controlled
    )
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.select({});
   * @description
   * Creates a selection element.
   * @descriptionRu
   * Создаёт селектор.
   * @example
   * UI.select({
   *  options: [{
   *    key: '000071aa-fe2d-436a-bb31-489d26f4e4b8',
   *    text: 'Column 1',
   *  }],
   *  value: '',
   *  onChange: function (value) {
   *    console.log('Selected value', value);
   *  },
   * })
   * @param param
   */
  public select(param: SelectWithDropdownProps): UIElement {
    const res = new SelectWrapper(this.context.box.companyId, param)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.dateInput({onChange: (value: Date) => {}});
   * @description
   * Creates date select element.
   * @descriptionRu
   * Создаёт элемент выбора даты со временем.
   * @example
   * // all parameters are optional
   * UI.dateInput({
   *   onChange: (value: Date) => {}
   *   initialValue: new Date()
   *   disablePastDate: true
   *   showTimeInput: true
   * });
   */
  public dateInput(param: DateInputProps): UIElement {
    const res = new DateInputWrapper(this.context.box.companyId, param)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.panel();
   * @example
   * var t = Items.get('... task id');
   * var p = UI.panel();
   * t.ui.add(p);
   * p.style = {
   *   background: '#acf',
   *   color: '#572'
   * };
   * p.add(UI.text('First element'));
   * p.add(UI.text('Second element'));
   * p.add(UI.button('Third element'));
   * @description
   * Creates container (panel) for multiple elements.
   * @descriptionRu
   * Создает контейнер (panel) для разных элементов.
   */
  public panel(): UIElement {
    const res = new PanelEl(this.context.box.companyId)
    this.created.push(res)
    return res
  }

  /**
   * UI.renderReact(() => <div>...</div>);
   * @description
   * Renders a React component inside the UI.
   * @descriptionRu
   * Отображает компонент React внутри UI.
   * @param renderer
   */
  public renderReact(renderer: () => ReactElement) {
    const res = new ReactRenderContainer(this.context.box.companyId, renderer)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.openPopup('', UI.panel());
   * @example
   * var t = UI.text('Test content');
   * // open popup
   * UI.openPopup('Test title', t);
   *
   * // open popup with custom width in pixels
   * UI.openPopup('Test title', t, 300);
   *
   * // open popup with subtitle and other properties
   * UI.openPopup('Test title', t, 300, { subTitle: 'Subtitle', autoHeight: true, paddings: 16 });
   *
   * @description
   * Opens a popup with the specified title and content. Optional properties can be provided
   * to customize the appearance and behavior:
   * - width (number): Width of the popup in pixels. Can be provided as a standalone third parameter or within "props".
   * - subTitle (string): Subtitle displayed below the main title.
   * - autoHeight (boolean): If true, the popup height adjusts automatically to content.
   * - isNew (boolean): Indicates if the popup should be marked as new.
   * - paddings (0 | 16 | 24): Padding around the content in pixels.
   * - closeOnOverlayDisabled (boolean): If true, the popup cannot be closed by clicking outside the popup.
   * - onCloseBySubs (function): Callback function triggered when the popup is closed by click outside or by escape key.
   *
   * @descriptionRu
   * Открывает попап с заданными заголовком и содержимым. Дополнительные параметры можно передать
   * для настройки внешнего вида и поведения:
   * - width (число): Ширина попапа в пикселях. Может быть передана отдельным третьим параметром или в "props".
   * - subTitle (строка): Подзаголовок, отображаемый под основным заголовком.
   * - autoHeight (булево): Если true, высота попапа автоматически подстраивается под контент.
   * - isNew (булево): Указывает, что попап должен быть отмечен как новый.
   * - paddings (0 | 16 | 24): Отступы вокруг контента в пикселях.
   * - closeOnOverlayDisabled (булево): Если true, попап не может быть закрыт кликом за пределами попапа.
   * - onCloseBySubs (функция): Обработчик события закрытия попапа кликом за пределами попапа или клавишей Escape.
   */
  public openPopup(
    title: string,
    element: UIElement,
    width?: number,
    props?: {
      width?: number
      subTitle?: string
      autoHeight?: boolean
      isNew?: boolean
      paddings?: 0 | 16 | 24
      closeOnOverlayDisabled: false
      onCloseBySubs?: () => void
    }
  ): void {
    ApiPopupCtl.open({title, element, width, ...props})
  }

  /**
   * @code
   * UI.openDialog('', UI.panel());
   * @example
   * UI.openDialog(UI.text('content'), {
   * title: 'Интеграция с Telegram',
   * width: 600,
   * primaryBtnText: 'Создать интеграцию',
   * submit: () => {
   *   console.log('submit')
   * }
   * })
   * @description
   * Creates a popup with preset buttons at the bottom
   * @descriptionRu
   * Создает попап с предустановленными кнопками снизу
   */
  public openDialog(
    element: UIElement,
    props: {
      submit: () => void
      close: () => void
      cancel?: () => void
      type?: 'two-buttons' | 'one-button'

      title: string
      subtitle?: string
      width?: number
      children?: React.ReactNode
      inlineDisplay?: boolean

      isDelete?: boolean
      primaryBtnText?: string
      closeBtnText?: string
      primaryBtnDisabled?: boolean
      skipSubmitBySpace?: boolean
      popupInWnd?: boolean
      noScroll?: boolean
      hidePrimaryBtn?: boolean
      headerStyle?: CSSProperties

      noShowMore?: boolean
      noShowMoreDefaultValue?: boolean
      noShowMoreText?: string
      noShowMoreTextCb?: (value: boolean) => void
      footer?: JSX.Element
    }
  ): void {
    ApiDialogCtl.open({...props, element})
  }

  public rerenderDialog(props: DialogProps) {
    ApiDialogCtl.rerender(props)
  }

  /**
   * @code
   * UI.setTaskInfoEl('', UI.panel());
   * @example
   * var t = UI.text('Test content');
   * // set element to task into section
   * UI.setTaskInfoEl(t);
   *
   * @description
   * Set element to task into section with specified content.
   * @descriptionRu
   * Вставляет элемент в секцию taskInfo
   */
  public setTaskInfoEl(element: UIElement): void {
    ApiTaskInfoCtl.setEl({element})
  }

  /**
   * @code
   * UI.setBoardSettingsEl('elementId', element);
   * @example
   * const element = UI.text('Шаблоны форм контент');
   * UI.setBoardSettingsEl('ext-forms', element);
   * @description
   * Sets a specific UI element for the board settings.
   * @descriptionRu
   * Устанавливает определённый элемент UI для настроек доски.
   * @param {string} id - The ID of the board setting element.
   * @param {UIElement} element - The UI element to be set for the board settings.
   */
  public setBoardSettingsEl(id: string, element: UIElement): void {
    ApiBoardSettingsCtl.setEl({id, element})
  }

  /**
   * @code
   * UI.closePopup();
   * @example
   * var t = UI.text('Test content');
   * UI.openPopup('Test title', t);
   *
   * // closing opened popup
   *  UI.closePopup();
   * @description
   * Closes opened popup.
   * @descriptionRu
   * Закрывает открытый попап.
   */
  public closePopup(): void {
    ApiPopupCtl.close()
  }

  /**
   * @code
   * UI.rawHtml('... html');
   * @example
   * var t = Items.get('... task id');
   * var html = \`
   *   Hi, <b>there</b>!
   *   <div id="my-el" onclick="alert('qwe')" style="color: red;">
   *     Click here!
   *   </div>
   *   <style>
   *     #my-el {
   *       background: #0ff;
   *     }
   *   </style>
   * \`; // for IE use regular string with '' instead of \`\`
   * t.ui.add(UI.rawHtml(html));
   * @description
   * Creates element with the specified html.
   * All the scripts and styles inside this html will work.
   * So it is not safe to pass user-generated data as html to this component.
   * @descriptionRu
   * Создает элемент с указанным html-кодом.
   * Все скрипты и стиле внутри этого html-кода будут работать.
   * Таким образом, не безопасно передавать пользовательские данные
   * как html в этот компонент.
   */
  public rawHtml(html: string): UIElement {
    const res = new RawHtmlEl(this.context.box.companyId, html)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.safeHtml('... html');
   * @example
   * var t = Items.get('... task id');
   * var html = \`
   *   Hi, <b>there</b>!
   *   <br>
   *   <ul>
   *     <li>one</li>
   *     <li>two</li>
   *   </ul>
   * \`; // for IE use regular string with '' instead of \`\`
   * t.ui.add(UI.rawHtml(html));
   * @description
   * Creates element with the specified html, html is cleaned from
   * potentially unsafe tags, so it can not execute scripts.
   * @descriptionRu
   * Создает элемент с указанным html. html очищается от потенциально
   * небезопасных тэгов, так что скрипты здесь не могут быть запущены.
   */
  public safeHtml(html: string): UIElement {
    const res = new SafeHtmlEl(this.context.box.companyId, html)
    this.created.push(res)
    return res
  }

  /**
   * @code
   * UI.selectText('... html element');
   * @example
   * var text = UI.text();
   * text.onRender = function () {
   *   UI.selectText(text.getElement());
   * };
   * @description
   * Selects all text inside html element.
   * @descriptionRu
   * Выделяет весь текст внутри html элемента.
   */
  public selectText(element: HTMLElement): void {
    if (!element) {
      return
    }
    const ieDoc = document as any
    if (window.getSelection && document.createRange) {
      const selection = window.getSelection()
      const range = document.createRange()
      range.selectNodeContents(element)
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(range)
      }
    } else if (ieDoc.selection && ieDoc.body.createTextRange) {
      const range = ieDoc.body.createTextRange()
      range.moveToElementText(this)
      range.select()
    }
  }

  /**
   * @code
   * const url = UI.getRightUrl('... url to public file');
   * @param url
   * @description
   * Returns the correct URL for a public file.
   * @descriptionRu
   * Возвращает правильный URL для публичного файла.
   */
  public getRightUrl(url: string): string {
    return getRightUrl(url)
  }

  /**
   * @code
   * const url = UI.getPublicUrl('filename.png');
   * @description
   * Returns the correct URL for a public file relative current extension.
   * @descriptionRu
   * Возвращает правильный URL для публичного файла относительно текущего расширения.
   * @param filename
   */
  public getPublicUrl(filename: string): string {
    if (!this.context.extensionId) {
      return ''
    }
    return getExtensionPublicUrl(this.context.extensionId, filename)
  }

  /**
   * @code
   * const monitor = UI.monitorUserActivity();
   * monitor.isUserActive();
   */
  public monitorUserActivity(): UserActivityMonitor {
    return UserActivityTracker.acquire()
  }

  /**
   * @code
   * UI.setBlockVisibility('company-departments', false)
   * UI.setBlockVisibility('company-users', false)
   * @description
   * Returns the correct URL for a public file.
   * @descriptionRu
   * Скрывает часть интерфейса в yougile
   */
  public setBlockVisibility(
    type: 'company-departments' | 'company-users',
    value: boolean
  ): void {
    apiBlockVisibilityStore.setVisibility(type, value)
  }

  public override beforeUnload(): void {
    this.created.forEach((el: UIElement) => {
      el.remove()
    })
    this.created = []
  }

  public generateUUID(): UUID {
    return Util.UUID()
  }

  /**
   * @experimental
   * @code
   * UI.embedComponentToSlot('chat-input-actions', () => <AiButton />)
   * @description
   * Allows to embed a custom React component into a React slot.
   * @descriptionRu
   * Позволяет встраивать пользовательский React компонент в слот.
   * @param slotName - name of the slot
   * @param key - unique key for the component
   * @param component - React component to embed
   * @returns Function to remove the component from the slot
   */
  public embedComponentToSlot(
    slotName: UiSlotName,
    component: React.ComponentType<{data: any}>
  ): Disposer {
    const disposer = UIRegistry.internalRegisterSlotInjection(
      slotName,
      this.context.extensionId || `${Math.random()}`,
      component
    )

    /**
     * Automatically unregister the component from the slot at the end of the extension lifecycle,
     * while preserving the ability for manual unbinding.
     */
    const disposeDisposer = this.addDisposeHandler(disposer)

    return () => {
      disposeDisposer()
      disposer()
    }
  }

  /**
   * @code
   * // Single file choose
   * UI.chooseFile(files, false, (file) => { console.log('Choosed file', file) })
   *
   * // Multiple files choose
   * UI.chooseFile(files, true, (file) => { console.log('Choosed file', file) })
   *
   * @description
   * Allows to choose one or multiple files from disk
   * @descriptionRu
   * Позволяет выбрать один или несколько файлов с диска
   * @param mouseEvent - event from onClick listener, used to dynamically build files picker
   * @param allowMultiple - allows to control multiple choose
   * @param onChoosed - callback to handle each choosed file separately
   */
  public chooseFile(
    mouseEvent: React.MouseEvent,
    allowMultiple = false,
    onChoosed: (file: File) => void
  ): Promise<File> {
    return FileChooser.chooseFile(mouseEvent, allowMultiple, file => {
      if (file.size && file.size > Backend.uploadFileLimit * 1024 * 1024) {
        MessageBox.warn(Lang.msg.common.fileSizeWarn(Backend.uploadFileLimit))
        return
      }

      onChoosed(file)
    })
  }

  /**
   * @code
   * // Regular upload
   * const urls = await UI.uploadFiles(files)
   *
   * // Upload with formatting resulted urls e.g. to use in chat
   * const urls = await UI.uploadFiles(files, {previewEncodeRelativeUrl: true})
   *
   * @description
   * Allows to upload user files
   * @descriptionRu
   * Позволяет загрузить пользовательские файлы
   * @param files - list of files
   * @param props - additional props e.g. {previewEncodeRelativeUrl: true} to format result
   */
  public async uploadFiles(
    files: File[],
    {previewEncodeRelativeUrl = false}
  ): Promise<string[]> {
    const urlPromises: Promise<string>[] = []

    for (const file of Util.iter(files)) {
      if (file.size && file.size > Backend.uploadFileLimit * 1024 * 1024) {
        MessageBox.warn(Lang.msg.common.fileSizeWarn(Backend.uploadFileLimit))
        continue
      }

      urlPromises.push(FileUploadProgress.saveFile(file))
    }

    return Promise.all(urlPromises).then(urls => {
      if (previewEncodeRelativeUrl) {
        return urls.map(FilePreview.encodeRelativeUrl)
      }
      return urls
    })
  }

  /**
   * @experimental
   * @code
   * const popup = UI.openReactPopup({ close } => ({
   *   title: 'Test',
   *   content: <div>
   *     <button onClick={close}>Close me</button>
   *   </div>,
   * })
   * popup.update({ title: 'New title' }) // Update props
   * popup.close(); // Force close
   * @description
   * Allows to open a React popup with custom content.
   * @descriptionRu
   * Позволяет открывать попап с пользовательским React-контентом.
   * @param getProps
   */
  public openReactPopup(getProps: PropsGetter): OpenPopupReturnType {
    return openPopup(getProps)
  }

  /**
   * @experimental
   * @code
   * const store = UI.createStore({ count: 0 })
   * store.subscribe((state) => {
   *    // Do something with the state
   * }, true)
   * store.setState({ count: 1 })
   * @description
   * Creates an observable state store with the initial state (compatible with MobX/React).
   * @descriptionRu
   * Создает наблюдаемое хранилище состояния с начальным состоянием (совместимо с MobX/React).
   * @param initialState
   */
  public createStore<T>(initialState: T): StateStore<T> {
    return new StateStore(initialState)
  }

  getLocalStorageExtensionState = () => {
    try {
      const state = localStorage.getItem(
        `extension__${this.context.extensionId}`
      )
      if (state) {
        return JSON.parse(state)
      }
    } catch (error) {
      //
    }
    return {}
  }

  saveLocalStorageExtensionState = (state: unknown) => {
    try {
      localStorage.setItem(
        `extension__${this.context.extensionId}`,
        JSON.stringify(state)
      )
      return true
    } catch (error) {
      logger.error(error)
      return false
    }
  }
}
